import { IMAGE_VARIANTS } from './constants';
import { ExpandableStageFieldList, ProfileSettingsFormKeys } from './enums';
import { isArrayLength } from './genericHelpers';
import { createImageVariantConfig } from './sdkLoader';

export function filterBuildingSystemOptions(buildingSystemOptions) {
  return (buildingSystemOptions || []).reduce((acc, option) => {
    // Skip 'SURPRISE_ME' option
    if (option.key === ProfileSettingsFormKeys.SURPRISE_ME) {
      return acc;
    }

    // Transform 'OTHER' option label
    const transformedOption =
      option.key === ProfileSettingsFormKeys.OTHER ? { ...option, label: 'Other' } : option;

    return [...acc, transformedOption];
  }, []);
}

export function generateContactFormHTMLForCostCalculator({
  floorArea,
  location,
  buildingFabricPerformance,
  finishQuality,
  costRange,
}) {
  return `
<div style="font-family:Arial,sans-serif; background-color:#f4f6f8; padding:20px;">
  <div style="max-width:600px; margin:0 auto; background-color:#ffffff; border-radius:8px; overflow:hidden; box-shadow:0 2px 4px rgba(0,0,0,0.1);">
    <div style="background-color:#C75300; color:#ffffff; padding:16px; text-align:center;">
      <h2 style="margin:0; font-size:18px;">🏗️ New Cost Calculator Submission</h2>
    </div>
    <div style="padding:20px; color:#333333;">
      <div style="margin-bottom:12px;">
        <strong>Floor Area:</strong> ${floorArea} m²
      </div>
      <div style="margin-bottom:12px;">
        <strong>Location:</strong> ${location}
      </div>
      <div style="margin-bottom:12px;">
        <strong>Building Fabric Performance:</strong> ${buildingFabricPerformance}
      </div>
      <div style="margin-bottom:12px;">
        <strong>Finish Quality:</strong> ${finishQuality}
      </div>
      <div style="margin-bottom:12px;">
        <strong>Estimated Cost Range:</strong> ${costRange}
      </div>
    </div>
    <div style="border-top:1px solid #eeeeee; font-size:12px; color:#888888; text-align:center; padding:12px;">
      This email was generated from your website’s cost calculator form.
    </div>
  </div>
</div>
`.trim();
}

/**
 * Generates all image variant configurations
 * @param {string} variantPrefix - Prefix for variant names
 * @param {number} aspectRatio - Height/Width ratio
 * @returns {Object} Combined variant configurations
 */
export const getImageVariantConfigs = (variantPrefix, aspectRatio) => ({
  ...createImageVariantConfig(variantPrefix, IMAGE_VARIANTS.SIZES.BASE, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-2x`, IMAGE_VARIANTS.SIZES.DOUBLE, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-4x`, IMAGE_VARIANTS.SIZES.QUAD, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-6x`, IMAGE_VARIANTS.SIZES.SEXTUPLE, aspectRatio),
});

export const buildInitialValuesFromDocs = (costEstimationDocuments, isPostEditVersion = false) => {
  const initialValues = {
    supplierMeta: {},
    stage1: {},
    stage2: {},
    stage3: {},
    stage4: {},
    stage5: {},
    professionalFees: {},
    property: null,
    propertySize: null,
    foundationSize: null,
    projectArea: null,
  };

  if (!Array.isArray(costEstimationDocuments)) return initialValues;

  // Helper to filter out empty values
  const filterNonEmpty = data =>
    Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) => value !== undefined && value !== null && value !== ''
      )
    );

  costEstimationDocuments.forEach((doc, docIndex) => {
    const docValues = doc.values || {};

    // Determine suppliers to process based on mode
    const suppliers = isPostEditVersion
      ? Object.entries(docValues.supplierMeta || {}).map(([key, data], idx) => ({
          originalKey: key,
          mappedKey: `supplier${idx + 1}`,
          data: { supplierId: data.supplierId || '', supplierName: data.supplierName || '' },
        }))
      : [
          {
            originalKey: 'supplier1',
            mappedKey: `supplier${docIndex + 1}`,
            data: { supplierId: doc.supplierId || '', supplierName: doc.supplierName || '' },
          },
        ];

    suppliers.forEach(({ originalKey, mappedKey, data }) => {
      // Store supplier metadata
      initialValues.supplierMeta[mappedKey] = data;

      // Handle stage1 to stage5
      for (let stageNum = 1; stageNum <= 5; stageNum++) {
        const stageKey = `stage${stageNum}`;
        const stageData = filterNonEmpty(docValues[stageKey]?.[originalKey] || {});
        initialValues[stageKey][mappedKey] = stageData;

        // Handle expandable fields in stage5
        if (stageNum === 5) {
          ExpandableStageFieldList.forEach(field => {
            const entries = docValues[stageKey]?.[field]?.entries || [];
            initialValues[stageKey][field] = initialValues[stageKey][field] || { entries: [] };

            entries.forEach((entry, entryIndex) => {
              initialValues[stageKey][field].entries[entryIndex] =
                initialValues[stageKey][field].entries[entryIndex] || {};
              const entryValue = entry[originalKey];
              if (entryValue !== undefined && entryValue !== null && entryValue !== '') {
                initialValues[stageKey][field].entries[entryIndex][mappedKey] = entryValue;
              }
            });
          });
        }
      }

      // Handle professionalFees
      const professionalFeesData = filterNonEmpty(docValues.professionalFees?.[originalKey] || {});
      initialValues.professionalFees[mappedKey] = professionalFeesData;
    });

    // Top-level shared fields (moved outside the loop)
    initialValues.property = docValues.property || initialValues.property;
    initialValues.propertySize = docValues.propertySize || initialValues.propertySize;
    initialValues.foundationSize = docValues.foundationSize || initialValues.foundationSize;
    initialValues.projectArea = docValues.projectArea || initialValues.projectArea;
  });

  return initialValues;
};
export const getLatestCostEstimation = (documents, isPostEditVersion) => {
  if (!isArrayLength(documents)) return [];

  // Sort by:
  // 1. post-edit versions first (true > false)
  // 2. then by createdAt (descending)
  const sortedDocuments = documents
    .filter(doc => doc?.createdAt?._seconds != null)
    .sort((a, b) => {
      // First: prioritize isPostEditVersion === true
      if (a.isPostEditVersion === b.isPostEditVersion) {
        return b.createdAt._seconds - a.createdAt._seconds;
      }
      return a.isPostEditVersion === true ? -1 : 1;
    });

  if (isPostEditVersion) {
    // When query param is present: return one edited, one original
    const latestPostEdit = sortedDocuments
      .filter(doc => doc.isPostEditVersion === true)
      .slice(0, 1)
      .map(doc => ({ ...doc, versionTag: 'Current Version' }));

    const latestOriginal = sortedDocuments
      .filter(doc => doc.isPostEditVersion === false)
      .slice(0, 1)
      .map(doc => ({ ...doc, versionTag: 'Previous Version' }));

    return [...latestPostEdit, ...latestOriginal];
  }

  // Default: return the highest priority doc (edited > newer original)
  return sortedDocuments.slice(0, 1);
};

/**
 * Get the count of valid suppliers in supplierMeta.
 * A supplier is considered valid if it matches the pattern "supplier<number>",
 * is an object, and has both supplierId and supplierName.
 * @param {Object} supplierMeta - The supplierMeta object (e.g., { supplier1: { supplierId: "...", supplierName: "..." }, ... })
 * @returns {number} The number of valid suppliers
 */
export const getSupplierCount = supplierMeta => {
  const meta = supplierMeta || {};
  return Object.keys(meta).filter(
    key =>
      /^supplier\d+$/.test(key) &&
      meta[key] &&
      typeof meta[key] === 'object' &&
      meta[key].supplierId &&
      meta[key].supplierName
  ).length;
};

/**
 * Get the keys of suppliers in supplierMeta that match the pattern "supplier<number>".
 * @param {Object} supplierMeta - The supplierMeta object
 * @returns {string[]} Array of supplier keys (e.g., ["supplier1", "supplier2"])
 */
export const getSupplierKeys = supplierMeta => {
  return Object.keys(supplierMeta || {}).filter(key => /^supplier\d+$/.test(key));
};

/**
 * Get the numeric indices of suppliers from their keys.
 * @param {string[]} supplierKeys - Array of supplier keys (e.g., ["supplier1", "supplier2"])
 * @returns {number[]} Array of supplier indices (e.g., [1, 2])
 */
export const getSupplierIndices = supplierKeys => {
  return supplierKeys.map(key => Number(key.replace('supplier', '')));
};
export const costEstimationEmailTemplate = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  message,
  submitDate,
  inquiryType = 'Cost Estimation',
}) => {
  return `
    <!DOCTYPE html>
  <html>
    <body style="font-family: Helvetica, Arial, sans-serif; background-color: #f9f9f9; padding: 0; margin: 0;">
      <div style="max-width: 600px; margin: 40px auto; background-color: #ffffff; border: 1px solid #ddd; border-radius: 6px; box-shadow: 0 4px 10px rgba(0,0,0,0.06); overflow: hidden;">

        <div style="background-color: #f85757; color: white; padding: 24px; font-size: 22px; font-weight: bold; text-align: center;">
          New Cost Estimation Inquiry
        </div>

        <div style="padding: 24px; color: #333333; line-height: 1.6;">
          <p><strong style="display: inline-block; width: 140px;">Inquiry Type:</strong> ${inquiryType}</p>
          <p><strong style="display: inline-block; width: 140px;">First Name:</strong> ${firstName}</p>
          <p><strong style="display: inline-block; width: 140px;">Last Name:</strong> ${lastName}</p>
          <p><strong style="display: inline-block; width: 140px;">Email:</strong> ${email}</p>
          <p><strong style="display: inline-block; width: 140px;">Phone Number:</strong> ${phoneNumber}</p>
          <p><strong style="display: inline-block; width: 140px;">Submitted At:</strong> ${submitDate}</p>
          <p><strong style="display: inline-block; width: 140px;">Message:</strong>${message}</p>
        </div>

        <div style="background-color: #f85757; color: white; text-align: center; padding: 16px; font-size: 14px;">
          You received this message via the Cost Estimation Tool
        </div>
      </div>
    </body>
  </html>
  `;
};
